import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, Spinner } from '@easydent/shared-ui';
import { listTenants, type TenantRow } from '@/lib/api';

const STATUS_CLASS: Record<string, string> = {
  Active: 'bg-brand-50 text-brand-ink-strong',
  Trialing: 'bg-amber-50 text-amber-700',
  PastDue: 'bg-red-50 text-red-600',
  Suspended: 'bg-red-50 text-red-600',
  Cancelled: 'bg-subtle text-muted',
};

const TenantsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<TenantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    listTenants().then(setRows).catch(() => setError(t('tenants.loadError', "Could not load tenants.")));
  }, []);

  const filtered = useMemo(
    () => (rows ?? []).filter((r) => `${r.name} ${r.slug}`.toLowerCase().includes(q.toLowerCase())),
    [rows, q],
  );

  if (error) return <Alert variant="error">{error}</Alert>;
  if (!rows) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-body">{t('tenants.title', "Tenants")}</h1>
        <span className="text-sm text-subtle-ink">{rows.length}</span>
        <input
          placeholder={t('tenants.search', "Search…")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="ml-auto rounded-lg border border-line-strong px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        />
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-line-soft text-left text-xs uppercase tracking-wide text-subtle-ink">
            <tr>
              <th className="px-4 py-3">{t('tenants.clinic', "Clinic")}</th>
              <th className="px-4 py-3">{t('tenants.plan', "Plan")}</th>
              <th className="px-4 py-3">{t('tenants.statusHeader', 'Status')}</th>
              <th className="px-4 py-3 text-right">{t('tenants.cabinets', "Cabinets")}</th>
              <th className="px-4 py-3 text-right">{t('tenants.patients', "Patients")}</th>
              <th className="px-4 py-3 text-right">{t('suppliers.wallet', "Wallet")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr
                key={r.id}
                onClick={() => navigate(`/tenants/${r.id}`)}
                className="cursor-pointer border-b border-line-faint hover:bg-canvas"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-body">{r.name}{r.isDemo && <span className="ml-2 rounded bg-subtle px-1.5 py-0.5 text-[10px] text-muted">DEMO</span>}</div>
                  <div className="text-xs text-subtle-ink">{r.slug}</div>
                </td>
                <td className="px-4 py-3 text-body-soft">{r.planName}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[r.status] ?? 'bg-subtle text-muted'}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-right text-body-soft">{r.cabinets}</td>
                <td className="px-4 py-3 text-right text-body-soft">{r.patients}</td>
                <td className="px-4 py-3 text-right text-body-soft">
                  {new Intl.NumberFormat(undefined, { style: 'currency', currency: r.currency, maximumFractionDigits: 0 }).format(r.walletBalance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default TenantsPage;
